/* eslint-disable max-len */
export const en = {
  brand: 'Rubtsov',
  seo: {
    siteName: 'Mikhail Rubtsov — Software Developer',
    ogLocale: 'en_US',
    home: {
      title: 'Mikhail Rubtsov — Software Developer with 10+ years in IT',
      description: 'Software developer with 10+ years in IT — in the industry since 2013. Web apps, bots, parsers, integrations. Led development teams of up to 35 people, still hands-on with code.',
    },
    projects: {
      title: 'Projects — Mikhail Rubtsov',
      description: 'Pet projects and shipped products by Mikhail Rubtsov: fincollect.cz, py-py.ru, fants.ru, jeetpay.cc, jeetbot.cc, mew-craft.ru, royal-shine.ru, 1-2dev.ru.',
    },
  },
  contacts: {
    telegramUrl: 'https://t.me/MrEarnestWhim',
    telegramHandle: '@MrEarnestWhim',
    email: '5494567@gmail.com',
    phoneDisplay: '+7 (939) 7000-772',
    phoneHref: 'tel:+79397000772',
  },
  header: {
    about: 'About me',
    steps: 'What I build',
    projects: 'Projects',
    contact: 'Get in touch',
    toHome: 'Back home',
    langSwitch: 'RU',
  },
  preview: {
    name: 'Mikhail Rubtsov',
    tagline: 'Software developer with 10+ years in IT — in the industry since 2013',
    subline: 'Led development teams of up to 35 people, still hands-on with code',
    ctaTelegram: 'Message on Telegram',
    ctaEmail: 'Send an email',
    stats: [
      { value: '10+', label: 'years in IT' },
      { value: '35+', label: 'people on the team' },
      { value: '20+', label: 'products from scratch' },
      { value: '2013', label: 'in the industry since' },
    ],
  },
  about: {
    heading: 'About me',
    parts: [
      { text: "I'm a developer and a manager in one. What I love most is building systems from scratch — architecture, teams and processes. In recent years I led a development direction, and now I'm open to a developer or a manager role. I write in " },
      { chip: true, color: 'green', text: 'Python' },
      { text: ', ' },
      { chip: true, color: 'purple', text: 'JavaScript' },
      { text: ', ' },
      { chip: true, color: 'green', text: 'PHP' },
      { text: ' and ' },
      { chip: true, color: 'purple', text: 'C/C++' },
      { text: ', and I pick up new things fast.' },
    ],
  },
  experience: {
    jobs: [
      {
        company: '7Tech LLC',
        role: 'Head of Development',
        period: 'Mar 2023 — Jun 2025',
        duration: '2 yrs 4 mos',
        highlights: 'Microservice architecture. Two cross-functional teams, 35+ people: hiring, roadmap, communication with CEO and investors.',
      },
      {
        company: 'Intelcon',
        role: 'Head of IT domain, architect',
        period: 'Dec 2020 — Feb 2023',
        duration: '2 yrs 3 mos',
        highlights: 'Built the IT direction from scratch: team of up to 32 people, two products launched.',
      },
      {
        company: 'OutGame',
        role: 'Tech Lead',
        period: 'Jul 2019 — Oct 2020',
        duration: '1 yr 4 mos',
        highlights: 'Architecture and microservice backend of the platform from scratch, Steam trading bots. Hired 2 backend developers.',
      },
      {
        company: 'Squilla Fund',
        role: 'Lead developer-architect',
        period: 'Nov 2018 — Jul 2019',
        duration: '9 mos',
        highlights: 'Custom oAuth service (LDAP-like), SDK for an Ethereum node, parsing big data into graph models.',
      },
    ],
    projectsLink: 'See projects →',
  },
  steps: {
    items: [
      {
        headingParts: ['My stack'],
        list: [
          'Python',
          'JavaScript',
          'C/C++',
          'PHP',
        ],
      },
      {
        headingParts: ['What I built'],
        list: [
          'Bots (incl. Steam trade)',
          'Parsers',
          'Exchange integrations and adjustments',
          'Websites (web apps)',
          'Real-time chats',
          'Integrations (REST and unofficial)',
          'Reverse engineering',
          'CRM, MDM, medical-class and many other system types',
          'Desktop clients',
          'Game plugins and mods',
          'Browser extensions',
          'Ethereum smart contracts',
        ],
      },
      {
        headingParts: ['And more'],
        list: [
          'Worked across different methodologies, task trackers, teams and tools. Hired... all kinds of people too ¯\\_(ツ)_/¯',
          'I use everything that helps or speeds up development/management. First books, then Google, now AI, tomorrow AGI ¯\\_(ツ)_/¯',
        ],
      },
    ],
  },
  contact: {
    heading: 'Get in touch',
    labels: { telegram: 'Telegram', email: 'Email', phone: 'Phone' },
    noteStart: 'Any channel works — pick whichever suits you: message me on ',
    noteMiddle: ' or drop an ',
    chipTelegram: 'telegram',
    chipEmail: 'email',
  },
  projectsPage: {
    heading: 'Projects',
    subline: 'Projects I shipped on my own or with a team — from an ATS platform to Minecraft server infrastructure.',
    back: '← Back home',
    projects: {
      'pypy-ats': {
        title: 'PyPy ATS',
        subtitle: 'Applicant Tracking System',
        description: 'A platform for managing the full recruiting cycle. From publishing vacancies and receiving applications to conducting interviews and hiring — all in one place.',
        features: [
          {
            title: 'Hiring pipeline',
            items: [
              'Kanban board with custom stages for each vacancy',
              'Drag-and-drop candidate movement between stages',
              'Linking candidates to multiple vacancies at once',
            ],
          },
          {
            title: 'Candidate management',
            items: [
              'Profiles with skills, resumes, contacts and notes',
              'Customizable statuses and workflows',
              'Test assignments with files and links',
              'Export of a candidate card to PDF',
            ],
          },
          {
            title: 'Interview scheduling',
            items: [
              'Work schedule accounting for breaks and weekends',
              'Automatic calculation of free slots',
              'Protection against schedule overlaps',
              'Public booking page — candidates pick a time themselves',
            ],
          },
          {
            title: 'Careers page',
            items: [
              'Public list of open vacancies',
              'Application form with resume upload',
              'Automatic candidate creation from an application',
            ],
          },
          {
            title: 'Questionnaires and assessment',
            items: [
              'Questionnaire builder with question types',
              'Attaching questionnaires to interviews',
              'Response and result tracking',
            ],
          },
          {
            title: 'Notifications and analytics',
            items: [
              'Telegram notifications about new applications and interviews',
              'Customizable email templates with variables',
              'Analytics dashboard: conversion funnel, daily charts',
            ],
          },
        ],
        screenshotAlts: [
          'Landing page',
          'Candidate kanban board',
          'Interview scheduling',
          'Careers page',
          'Analytics',
          'Settings and templates',
        ],
      },
      fants: {
        title: 'Fants.ru',
        subtitle: 'A game platform for couples and groups',
        description: 'An entertainment platform with a collection of interactive games and psychological tests. Real-time multiplayer, progression system, chat and blog — a full-fledged product with SEO.',
        features: [
          {
            title: '7 interactive games',
            items: [
              'Fants — leveled tasks with timers and images',
              'Truth or Dare — classic mechanics with a pool of 100+ questions',
              'Never Have I Ever — game modes, lie detector, anonymous answers',
              '52 tasks for a year — cards with modifiers and jokers',
              'Sexopoly — a board game with cell capture and points',
              'Wheel of Desires — heat levels, bonus cards, safe word',
              'Erotic dice — default and custom sets',
            ],
          },
          {
            title: '6 psychological tests',
            items: [
              'Liberation test — 3 question blocks, percentile against the database',
              'What kind of lover are you — 4 assessment categories, multidimensional result',
              'Compatibility test — paired completion, 5 analysis blocks',
              'Sexual superpower — 8 types with compatibility and techniques',
              'Sexual age — a quick assessment of experience level',
              'Secret desire — 7 categories with psychology and recommendations',
            ],
          },
          {
            title: 'Game mechanics',
            items: [
              'Multiplayer for 2-4 players with turn tracking',
              'Difficulty levels and progression within a session',
              'Password protection — share a game without partner registration',
              'Game and test result history in the personal account',
            ],
          },
          {
            title: 'Users and profiles',
            items: [
              'Email registration with verification',
              'Profile with avatar, interests and settings',
              'Password reset and session management',
              'Cookie consent and GDPR compliance',
            ],
          },
          {
            title: 'Chat and content',
            items: [
              'Private and group chats with image sharing',
              'Invite links for group chats',
              'Blog with CKEditor, SEO optimization and view counter',
              'FAQ with JSON-LD markup for search engines',
            ],
          },
          {
            title: 'Analytics and monitoring',
            items: [
              'Client-side logging of JS errors and blockers',
              'Tracking of external referrers and traffic sources',
              'Email campaign logging with statuses',
              'Sentry for errors, Prometheus for metrics',
            ],
          },
        ],
        screenshotAlts: [
          'Home page',
          'Gameplay — Fants',
          'Test result',
          'Chat',
          'Blog',
          'User profile',
        ],
      },
      'royal-shine': {
        title: 'Royal Shine',
        subtitle: 'A beauty studio platform with online booking and master accounts',
        description: 'A full-fledged platform for a beauty salon: clients book online through a step-by-step widget, while masters manage schedules, appointments, services and portfolios via a personal account. Telegram notifications, flexible work schedules and automatic slot calculation.',
        features: [
          {
            title: 'Online booking for clients',
            items: [
              'Step-by-step widget: category → service → master → available date and time',
              'Automatic calculation of available slots accounting for occupancy and breaks',
              'Preparation and cleanup time — buffer between appointments',
              'Appointment confirmation and cancellation',
            ],
          },
          {
            title: 'Master account',
            items: [
              "Dashboard: today's/tomorrow's appointments, weekly and monthly statistics",
              'Appointment management — create, edit, confirm, cancel',
              'Client base with visit history, appointment counts and totals',
              'Portfolio management — work photo upload with moderation',
              'Service CRUD: name, price, duration, related services',
            ],
          },
          {
            title: 'Flexible schedule',
            items: [
              'Weekly schedule — hours configured per weekday',
              'Shift schedule — X days working / Y days off cycle',
              'Lunch breaks and personal time-offs',
              'Working days preview 14 days ahead',
              'Booking horizon setting — how many days ahead clients can book',
            ],
          },
          {
            title: 'Service catalog',
            items: [
              'Service categories: nails, brows, lashes and more',
              'Each service — with price, duration and description',
              'Related services — cross-selling in the price list',
              'Activation/deactivation and a "price list only" flag for non-bookable services',
            ],
          },
          {
            title: 'Work portfolio',
            items: [
              'Gallery with category filtering',
              'Automatic generation of thumbnails and enlarged versions',
              'Moderation — the master uploads, the admin publishes',
              'Tags and ALT descriptions for SEO',
            ],
          },
          {
            title: 'Notifications and integrations',
            items: [
              'Telegram bot — instant notifications about new appointments',
              'Docker containerization for deployment',
              'Telegram service as a separate microservice',
            ],
          },
        ],
        screenshotAlts: [
          'Studio home screen',
          'Online booking widget',
          'Master dashboard',
          'Schedule management',
          'Service catalog',
          'Work portfolio',
        ],
      },
      mewcraft: {
        title: 'mewCraft',
        subtitle: 'Cross-platform launcher and Minecraft project infrastructure',
        description: 'Complete infrastructure for a Minecraft project: a C++/Qt desktop launcher with mod-pack auto-updates, a Django backend with its own Mojang API-compatible authentication system, a landing page with live server status, a personal account with skin/cape upload and a hierarchical FAQ system.',
        features: [
          {
            title: 'Desktop launcher (C++/Qt 6)',
            items: [
              'Cross-platform — Windows, macOS, Linux from a single codebase',
              'JWT authorization with secure storage (Keychain / Credential Manager / libsecret)',
              'Automatic mod-pack installation and updates via manifest with SHA-256 verification',
              'Parallel file downloads with progress bar and retries',
              'Auto-detection and installation of the required Java version',
              'RAM, Java arguments and launch parameter settings',
            ],
          },
          {
            title: 'API backend (Django + DRF)',
            items: [
              'JWT authentication with refresh token rotation and rate limiting',
              'Server management — public and private with per-user access and expirations',
              'Mod-pack system — manifests with hashes, versioning, CLI generation from a directory',
              'Secured file delivery via nginx X-Accel-Redirect',
              'Launcher update checks via API',
              'Health check endpoint (DB + cache)',
            ],
          },
          {
            title: 'Mojang-compatible Auth API',
            items: [
              'Full session/join + hasJoined implementation for multiplayer',
              'Player profiles with textures (UUID lookup, batch resolve)',
              'Skin and cape delivery by UUID',
              'Player attributes, blocklist, publickeys — full compatibility with patched authlib',
            ],
          },
          {
            title: 'Web portal and personal account',
            items: [
              'Landing with real-time server online status (Server List Ping)',
              'Email registration with encrypted-link confirmation',
              'Personal account — skin and cape upload with PNG size validation',
              'Password reset via email',
            ],
          },
          {
            title: 'FAQ / Wiki system',
            items: [
              'Hierarchical article tree on django-mptt',
              'CKEditor 5 for content editing',
              'Access separation — public, authorized, admin-only',
            ],
          },
          {
            title: 'DevOps and infrastructure',
            items: [
              'Docker Compose — PostgreSQL + Redis + Django + nginx',
              'nginx: API proxying, static, X-Accel-Redirect for pack files',
              '86% test coverage — auth, servers, access, manifests, files',
              'Ruff + mypy for linting and type checking',
            ],
          },
        ],
        screenshotAlts: [
          'Launcher — login screen',
          'Launcher — server list',
          'Launcher — mod-pack download',
          'Landing with servers',
          'Personal account — skins',
          'FAQ / Wiki',
        ],
      },
      fincollect: {
        title: 'Fincollect',
        subtitle: 'Website for a Czech debt collection agency (Prague)',
        description: 'Corporate platform for Fincollect SE, a receivables management agency. Separate flows for debtors and partners, reference sections and contact forms.',
        features: [
          {
            title: 'Content structure',
            items: [
              'Separate flows for clients and partners',
              'FAQ with answers to common questions',
              'Glossary of legal terms in plain language',
            ],
          },
          {
            title: 'Requests and contacts',
            items: [
              'Contact forms for clients',
              'Contact block with phone, address and email',
              'Personal data policy page (GDPR)',
            ],
          },
        ],
        screenshotAlts: [
          'Home page',
          'For clients section',
          'For partners section',
          'FAQ and glossary',
        ],
      },
      jeetdonate: {
        title: 'JeetDonate',
        subtitle: 'Donation platform for streamers (jeetpay.cc)',
        description: 'A donation service for streamers: customizable OBS widgets, real-time notifications over WebSocket and donation page management from a personal dashboard.',
        features: [
          {
            title: 'OBS widgets',
            items: [
              'GIFs, sounds and animations — fully customizable to the stream style',
              'Donation alerts delivered to the widget over an active WebSocket connection',
            ],
          },
          {
            title: 'Personal dashboard',
            items: [
              'Finances: balance, fees and transaction history',
              'Minimum donation amount setting',
              'Donation page appearance management',
            ],
          },
        ],
        screenshotAlts: [
          'Landing page',
          'Streamer dashboard',
          'Widget settings',
          'Donation page',
        ],
      },
      jeetbot: {
        title: 'JeetBot',
        subtitle: 'Bot for Twitch, Kick and VK Live',
        description: 'A multifunctional streaming bot: chat message speech synthesis, a moderator overlay, auto-translation and dozens of utilities. Subscriptions for streamers and viewers.',
        features: [
          {
            title: 'Text-to-speech (TTS)',
            items: [
              'Chat messages read aloud on stream',
              'Large voice selection, custom voices via subscription',
            ],
          },
          {
            title: 'Mod Alerts — moderator overlay',
            items: [
              'Push images, GIFs and text to screen straight from the browser',
              'Customizable canvas with OBS sync',
            ],
          },
          {
            title: 'Chat utilities',
            items: [
              'Punto Switcher — fixes forgotten keyboard layouts',
              'Auto-pin a message for the duration of the stream',
              'Currency converter, Shazam, message auto-translation',
              'Mention notifications in Telegram',
            ],
          },
          {
            title: 'Monetization',
            items: [
              'Streamer subscriptions (Starter / Streamer / Ultimate)',
              'Viewer subscription: personal TTS limit, premium voices and icons',
            ],
          },
        ],
        screenshotAlts: [
          'Landing page',
          'Control panel',
          'TTS settings',
          'Mod Alerts overlay',
        ],
      },
    },
  },
  notFound: {
    title: '404 — page not found',
    text: "This page doesn't exist. Head to the home page — everything important is there.",
    back: '← Back home',
  },
};
